/**
 * Live state for one round.
 *
 * The rule this hook exists to enforce: **a snapshot replaces state, an event
 * appends to it.** Events can be missed — the bus is `LISTEN`/`NOTIFY` and does
 * not replay — so anything that tried to reconstruct the draw log from
 * `round.draw` frames alone would silently diverge the first time a phone
 * changed networks, and would keep running as if it were right. Re-subscribing
 * yields a fresh snapshot, and the snapshot always wins.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client.ts';
import type { CardLayout, CardView, RoundView } from '../api/types.ts';
import { GameSocket, type ConnectionState, type ServerFrame } from './socket.ts';
import { haptics } from '../telegram.ts';

export interface RoundOutcome {
  kind: 'won' | 'lost' | 'cancelled';
  /** The current viewer's own share, when `kind === 'won'`. */
  amount?: string;
  reason?: string;
  /**
   * The (first) winner's first name — set for `won` and `lost` alike, since
   * §4's shared announcement ("Hayat won ETB 1,255.00") is shown to every
   * viewer, not just the winner.
   */
  winnerFirstName?: string;
  /** The first winner's picker number (1-based) — shown alongside their name. */
  winnerCartelaNumber?: number;
  /** The first winner's share — always present alongside `winnerFirstName`. */
  winnerAmount?: string;
  /** The first winner's actual card, and which combo on it won — shown to every viewer. */
  winnerLayout?: CardLayout;
  winnerCombo?: number[];
  /** Set only on a split-pot round (§4 dual winner) where a second card also won. */
  secondWinner?: {
    firstName: string;
    cartelaNumber: number;
    amount: string;
    layout: CardLayout;
    combo: number[];
  };
  /** True when the current viewer *is* `secondWinner`, not the first winner. */
  youAreSecondWinner?: boolean;
}

export interface RoundState {
  round: RoundView | null;
  calledNumbers: number[];
  /**
   * The same numbers as a set, so `Cartela` can test 25 cells in O(1) without
   * rebuilding it on every render.
   */
  calledSet: ReadonlySet<number>;
  cards: CardView[];
  /** Picker numbers already bought by someone, for the cartela picker's grey-out. */
  takenCartelas: ReadonlySet<number>;
  outcome: RoundOutcome | null;
  connection: ConnectionState;
  loading: boolean;
  error: string | null;
  /** Re-reads everything over HTTP — used after a purchase changes our cards. */
  refresh(): Promise<void>;
}

/**
 * Folds a server list of taken cartela numbers into what the picker is already
 * showing — **union only, a number never goes back to free.**
 *
 * The exception to this file's "a snapshot replaces state" rule, and the one
 * place it is deliberately broken. Occupancy churns underneath a round for
 * reasons that are true but unexplainable at the grid: a real buyer evicts a
 * phantom (`applyArifSeatAllocation`), the weekly dispersal trims a round's
 * phantom headcount, a player leaves before the start. Each of those frees a
 * number, and replacing the set wholesale made cells flip from grey back to
 * tappable while someone was looking at them — a number taken one second and
 * free the next, over and over. To a player who uses the app every week that
 * reads as the board being faked, which is the single worst thing this screen
 * can say (2026-08-27, operator's request).
 *
 * What it costs: a seat freed by an eviction or a leave stays greyed out for
 * anyone with the picker already open, until they reopen the round. One cell
 * that could have been bought and wasn't, against a grid that visibly
 * rewrites itself — the trade is not close. The set is cleared whenever the
 * hook switches rounds, so it never leaks one round's occupancy into another.
 */
function unionTaken(prev: ReadonlySet<number>, next: readonly number[]): ReadonlySet<number> {
  if (next.every((n) => prev.has(n))) return prev; // nothing new — keep the identity, skip the re-render
  const merged = new Set(prev);
  for (const n of next) merged.add(n);
  return merged;
}

export function useRound(roundId: string | null, myUserId: string | null): RoundState {
  const [round, setRound] = useState<RoundView | null>(null);
  const [calledNumbers, setCalled] = useState<number[]>([]);
  const [cards, setCards] = useState<CardView[]>([]);
  const [takenCartelas, setTakenCartelas] = useState<ReadonlySet<number>>(new Set());
  const [outcome, setOutcome] = useState<RoundOutcome | null>(null);
  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Read inside the frame handler, which is created once — a state variable
  // would be captured at its first value and every outcome would be "lost".
  const myUserIdRef = useRef(myUserId);
  myUserIdRef.current = myUserId;

  const socketRef = useRef<GameSocket | null>(null);

  const refresh = useCallback(async () => {
    if (!roundId) return;
    try {
      const [detail, mine, taken] = await Promise.all([
        api.round(roundId),
        api.myCards(roundId),
        api.takenCartelas(roundId),
      ]);
      setRound(detail.round);
      setCalled(detail.calledNumbers);
      setCards(mine.cards);
      setTakenCartelas((prev) => unionTaken(prev, taken.taken));
      setError(null);
    } catch {
      // Deliberately quiet: the socket is the primary channel and may well be
      // fine. Surfacing every failed background refresh as an error banner
      // would make a healthy session look broken.
    }
  }, [roundId]);

  useEffect(() => {
    if (!roundId) return;

    let cancelled = false;
    setLoading(true);
    setOutcome(null);
    // Occupancy is per round and `unionTaken` never shrinks the set, so this
    // is the one place it gets emptied — without it, the round you just left
    // would grey out cells in the one you just opened.
    setTakenCartelas(new Set());

    const handleFrame = (frame: ServerFrame): void => {
      switch (frame.type) {
        case 'round.snapshot':
          // Wholesale replacement. See the note at the top of this file.
          setRound(frame.round);
          setCalled(frame.calledNumbers);
          setCards(frame.cards);
          setLoading(false);
          setError(null);
          // The snapshot itself doesn't carry taken-cartela numbers, and a
          // reconnect gap may have missed `round.player_joined` frames — so
          // resync this one piece over HTTP rather than trusting whatever
          // was accumulated before the gap.
          void api
            .takenCartelas(roundId)
            .then((t) => {
              // Union, not replacement, even here: a reconnect gap is exactly
              // when the grid would rewrite itself in front of the player.
              if (!cancelled) setTakenCartelas((prev) => unionTaken(prev, t.taken));
            })
            .catch(() => {});
          return;

        case 'round.starting':
          setRound((prev) =>
            prev
              ? {
                  ...prev,
                  status: 'starting',
                  startsAt: frame.startsAt,
                  playerCount: frame.playerCount,
                }
              : prev,
          );
          return;

        case 'round.started':
          setRound((prev) =>
            prev
              ? {
                  ...prev,
                  status: 'live',
                  prizePool: frame.prizePool,
                  seedCommitment: frame.seedCommitment,
                  playerCount: frame.playerCount,
                }
              : prev,
          );
          return;

        case 'round.draw':
          setCalled((prev) => {
            // Idempotent against a duplicate frame, and against a snapshot that
            // already included this number after a reconnect.
            if (prev.includes(frame.number)) return prev;
            return [...prev, frame.number];
          });
          haptics.ball();
          return;

        case 'round.finished': {
          const iAmFirst = frame.winner.userId === myUserIdRef.current;
          const iAmSecond = frame.secondWinner?.userId === myUserIdRef.current;
          const won = iAmFirst || iAmSecond;
          setOutcome({
            kind: won ? 'won' : 'lost',
            // My own share when I won — the *other* winner's amount if I'm
            // the second one, never the first winner's when that's not me.
            amount: iAmSecond ? frame.secondWinner!.amount : frame.winner.amount,
            winnerFirstName: frame.winner.firstName,
            winnerCartelaNumber: frame.winner.entryIndex + 1,
            winnerAmount: frame.winner.amount,
            winnerLayout: frame.winner.layout,
            winnerCombo: frame.winner.winningCombo,
            ...(frame.secondWinner
              ? {
                  secondWinner: {
                    firstName: frame.secondWinner.firstName,
                    cartelaNumber: frame.secondWinner.entryIndex + 1,
                    amount: frame.secondWinner.amount,
                    layout: frame.secondWinner.layout,
                    combo: frame.secondWinner.winningCombo,
                  },
                  youAreSecondWinner: iAmSecond,
                }
              : {}),
          });
          setRound((prev) =>
            prev
              ? {
                  ...prev,
                  status: 'finished',
                  winnerUserId: frame.winner.userId,
                  winnerCardId: frame.winner.cardId,
                  ...(frame.secondWinner
                    ? {
                        winnerUserId2: frame.secondWinner.userId,
                        winnerCardId2: frame.secondWinner.cardId,
                      }
                    : {}),
                  rngSeed: frame.rngSeed,
                }
              : prev,
          );
          if (won) haptics.win();
          else haptics.lose();
          return;
        }

        case 'round.cancelled':
          setOutcome({ kind: 'cancelled', reason: frame.reason });
          setRound((prev) => (prev ? { ...prev, status: 'cancelled' } : prev));
          return;

        case 'round.player_joined':
          setRound((prev) =>
            prev
              ? { ...prev, playerCount: frame.playerCount, cardCount: frame.cardCount }
              : prev,
          );
          if (frame.cartelaNumber !== undefined) {
            const taken = frame.cartelaNumber;
            setTakenCartelas((prev) => (prev.has(taken) ? prev : new Set(prev).add(taken)));
          }
          return;

        default:
          return;
      }
    };

    const socket = new GameSocket({
      onFrame: handleFrame,
      onStateChange: (state) => {
        if (!cancelled) setConnection(state);
      },
    });
    socketRef.current = socket;
    socket.connect();
    socket.subscribe(roundId);

    // Load over HTTP in parallel rather than waiting for the socket. The
    // snapshot will overwrite this the moment it lands; showing a card a
    // half-second sooner is worth one redundant request.
    void api
      .round(roundId)
      .then(async (detail) => {
        if (cancelled) return;
        setRound((prev) => prev ?? detail.round);
        setCalled((prev) => (prev.length > 0 ? prev : detail.calledNumbers));
        const [mine, taken] = await Promise.all([
          api.myCards(roundId),
          api.takenCartelas(roundId),
        ]);
        if (cancelled) return;
        setCards((prev) => (prev.length > 0 ? prev : mine.cards));
        setTakenCartelas((prev) => unionTaken(prev, taken.taken));
      })
      .catch(() => {
        if (!cancelled) setError('load_failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      socket.close();
      socketRef.current = null;
    };
  }, [roundId]);

  const calledSet = useMemo(() => new Set(calledNumbers), [calledNumbers]);

  return {
    round,
    calledNumbers,
    calledSet,
    cards,
    takenCartelas,
    outcome,
    connection,
    loading,
    error,
    refresh,
  };
}
